import { Box, Grid, styled, useTheme } from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { PieChart } from '@mui/x-charts/PieChart'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { CenteredSpinner } from '../../components/Spinner'
import { useLocation } from 'react-router-dom'
import { MUIThemeColors } from 'src/mui-theme'
import { Card } from 'src/styles/Index'
import { getThemeColor } from 'src/utils'
import { useViewportHeight } from 'src/hooks/useViewportHeight'
import { useGetCustomersServiceStationsAdministrators } from '../../api/customer'
import NewsFeed from '../../components/news/NewsFeed'
import AllServiceStations from './container/AllServiceStations'

const ServiceStationMainPage = () => {
  const viewportHeight = useViewportHeight()
  const [open, setOpen] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const { data, isLoading, isSuccess } = useGetCustomersServiceStationsAdministrators(searchValue)
  const { hash } = useLocation()
  const theme = useTheme()

  const totalChartHeight = ((viewportHeight - 155) * 2) / 3
  const chartComponentHeight = Math.max(120, totalChartHeight / 2.3)
  const operationSummaryWidth = Math.max(200, Math.min(350, chartComponentHeight * 1.5))

  useEffect(() => {
    const currentCustomerId = localStorage.getItem('currentCustomerId')
    const customer = data?.find(c => c.id.toString() === currentCustomerId)
    if (currentCustomerId) {
      setOpen(parseInt(currentCustomerId))
      if (customer?.isAdminCustomer) localStorage.setItem('selectedCustomerAdminCustomerId', currentCustomerId)
    } else if (data && data?.length > 0) {
      setOpen(data[0]?.id)
      if (data[0]?.isAdminCustomer) localStorage.setItem('selectedCustomerAdminCustomerId', data[0]?.id.toString())
    }
  }, [data])

  useEffect(() => {
    setSearchValue(hash.replace('#', ''))
  }, [hash])

  return (
    <>
      {isLoading && <CenteredSpinner />}
      {isSuccess && (
        <Box
          className="relative z-10 h-full overflow-hidden"
          sx={{ width: '100%', backgroundColor: getThemeColor(theme, MUIThemeColors.white) }}
        >
          <Box className="pt-1">
            <Grid container>
              <Grid item md={7} lg={7} sm={12}>
                <AllServiceStations customers={data} open={open} setOpen={setOpen} />
              </Grid>
              <Grid item md={5} lg={5} sm={12} className="pl-16-core">
                <ChartContainer height={totalChartHeight}>
                  <Grid container>
                    <Grid item md={6} lg={6} sm={12}>
                      <OperationSummary width={operationSummaryWidth} height={chartComponentHeight} />
                    </Grid>
                    <Grid
                      item
                      md={6}
                      lg={6}
                      sm={12}
                      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <CustomerContribution height={chartComponentHeight} />
                    </Grid>
                    <Grid
                      item
                      md={12}
                      lg={12}
                      sm={12}
                      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <BarChartComponent
                        dataset={dataset}
                        chartSetting={chartSetting}
                        valueFormatter={valueFormatter}
                        height={chartComponentHeight}
                      />
                    </Grid>
                  </Grid>
                </ChartContainer>
                <Box className="pt-16-core">
                  <Card
                    className="p-4-core scrollbar"
                    sx={{
                      height: ((viewportHeight - 180) * 1) / 3 - 22,
                      boxShadow: 'none',
                      border: 'none',
                      overflowY: 'auto',
                    }}
                  >
                    {open ? (
                      <NewsFeed
                        customerId={open}
                        isCustomerAdmin={data?.find(c => c.id === open)?.isAdminCustomer}
                        hasLocalities={false}
                        hasServiceStations={data?.find(c => c.id === open)?.hasServiceStations}
                        hasStorages={false}
                      />
                    ) : null}
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </>
  )
}

export default ServiceStationMainPage

//styled components
export const ChartContainer = styled(Box)<{ height: number }>`
  padding: 9px 8px 9px 10px;
  border: 1px solid #d8e1e7;
  display: flex;
  flex-direction: row;
  border-radius: 5px;
  margin-top: 4px;
  height: ${props => props.height}px;
  background-color: ${props => getThemeColor(props.theme, MUIThemeColors.white)};
`

export const dataset = [
  { month: 'Jan', london: 30, paris: 20, newYork: 25, seoul: 15 },
  { month: 'Feb', london: 40, paris: 25, newYork: 30, seoul: 20 },
  { month: 'Mar', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'Apr', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'May', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'June', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'July', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'Aug', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'Sep', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'Oct', london: 35, paris: 30, newYork: 20, seoul: 25 },
  { month: 'Nov', london: 35, paris: 30, newYork: 20, seoul: 25 },
]

export const valueFormatter = value => `$${value}`

export const chartSetting = {
  width: 700,
  height: 250,
}

export function OperationSummary({
  uData = [19, 14, 31, 27, 50, 40, 40],
  pData = [24, 15, 30, 39, 48, 38, 38],
  iData = [5, 6, 5, 17, 7, 5, 3],
  xLabels = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July'],
  width = 500,
  height = 250,
}) {
  return (
    <LineChart
      width={width}
      height={height}
      series={[
        { data: pData, color: '#0074B2' },
        { data: uData, color: '#00526C' },
        { data: iData, color: '#00304A' },
      ]}
      xAxis={[{ scaleType: 'point', data: xLabels }]}
      sx={{ backgroundColor: '#ffffff' }}
    />
  )
}

OperationSummary.propTypes = {
  uData: PropTypes.arrayOf(PropTypes.number),
  pData: PropTypes.arrayOf(PropTypes.number),
  iData: PropTypes.arrayOf(PropTypes.number),
  xLabels: PropTypes.arrayOf(PropTypes.string),
  width: PropTypes.number,
  height: PropTypes.number,
}

export const CustomerContribution = ({ height = 250 }) => {
  return (
    <PieChart
      series={[
        {
          data: [
            { id: 0, value: 10, color: '#0074B2' },
            { id: 1, value: 15, color: '#00526C' },
            { id: 2, value: 20, color: '#00304A' },
          ],
        },
      ]}
      width={300}
      height={height}
    />
  )
}

export const BarChartComponent = ({ dataset, chartSetting, valueFormatter, height = 250 }) => {
  return (
    <BarChart
      dataset={dataset}
      xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
      series={[
        { dataKey: 'london', valueFormatter, color: '#0074B2' },
        { dataKey: 'paris', valueFormatter, color: '#00526C' },
        { dataKey: 'newYork', valueFormatter, color: '#00304A' },
        { dataKey: 'seoul', valueFormatter, color: '#838383' },
      ]}
      {...chartSetting}
      height={height}
    />
  )
}
